import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { teacherDisplayName } from "./signoutData";
import { fetchCheckIns } from "../shared/emergencyData";
import { activeClassOf } from "../shared/activeClass";
import "./Checklist.css";

// ── Formatting ─────────────────────────────────────────────────────────────
function fmtDuration(ms) {
  if (ms == null || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60), mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}
function fmtDateTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Recap of a single (usually ended) emergency: headline stats + per-class lock-in.
// Read-only — recomputed from the persisted emergency doc, its checkIns, and the
// current roster. (Per-class breakdown uses the school's CURRENT classes, a fine
// approximation for a recent drill.)
export default function EmergencyRecap({ emergency, onClose }) {
  const { schoolId } = useUser();
  const [checkIns, setCheckIns] = useState(null); // array | null while loading
  const [students, setStudents] = useState([]);
  const [classMap, setClassMap] = useState({});   // classId -> { className, teacherId, isArchived }
  const [teacherMap, setTeacherMap] = useState({}); // uid -> name

  useEffect(() => {
    if (!emergency?.id || !schoolId) return;
    let alive = true;
    (async () => {
      try {
        const [ci, sSnap, cSnap, uSnap] = await Promise.all([
          fetchCheckIns(emergency.id),
          getDocs(query(collection(firestore, "students"), where("schoolId", "==", schoolId))),
          getDocs(query(collection(firestore, "classes"), where("schoolId", "==", schoolId))),
          getDocs(query(collection(firestore, "users"), where("schoolId", "==", schoolId))),
        ]);
        if (!alive) return;
        setCheckIns(ci);
        setStudents(sSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const cm = {};
        cSnap.docs.forEach((d) => {
          const dd = d.data();
          cm[d.id] = { className: dd.className || "Class", teacherId: dd.teacherId, isArchived: dd.isArchived === true, isHomeroom: dd.isHomeroom === true, isSpecials: dd.classType === "specials" };
        });
        setClassMap(cm);
        const tm = {};
        uSnap.docs.forEach((d) => { tm[d.id] = teacherDisplayName(d.data()); });
        setTeacherMap(tm);
      } catch (e) {
        console.error("[EmergencyRecap] load error:", e);
        if (alive) setCheckIns([]);
      }
    })();
    return () => { alive = false; };
  }, [emergency?.id, schoolId]);

  const startMs = emergency?.startedAt ? new Date(emergency.startedAt).getTime() : null;
  const endMs = emergency?.endedAt ? new Date(emergency.endedAt).getTime() : null;

  // Same roster scoping as the live board: only students in a currently-active
  // (non-archived) class count toward totals; a student's class is their homeroom
  // if active, else their first active enrolled class.
  const activeClassIds = useMemo(
    () => new Set(Object.keys(classMap).filter((id) => !classMap[id].isArchived)),
    [classMap],
  );
  const homeroomClassIds = useMemo(
    () => new Set(Object.keys(classMap).filter((id) => classMap[id].isHomeroom && !classMap[id].isArchived)),
    [classMap],
  );
  const specialsClassIds = useMemo(
    () => new Set(Object.keys(classMap).filter((id) => classMap[id].isSpecials)),
    [classMap],
  );
  // Same grouping as the live board — shared helper so the recap can't disagree
  // with what teachers saw during the event. See shared/activeClass.js.
  const classOf = (s) => activeClassOf(s, activeClassIds, homeroomClassIds, specialsClassIds);

  const stats = useMemo(() => {
    if (checkIns == null) return null;
    const ciByStudent = {};
    checkIns.forEach((c) => { ciByStudent[c.studentId || c.id] = c; });

    const visible = students.filter((s) => classOf(s) !== null);
    const total = visible.length;
    const accountedList = visible.filter((s) => ciByStudent[s.id]);
    const accounted = accountedList.length;
    const missing = total - accounted;

    // Time-to-all-clear: only meaningful if everyone was accounted for. It's the
    // last (latest) check-in among the visible roster, measured from the start.
    let allClearMs = null;
    if (total > 0 && missing === 0 && startMs != null) {
      const lastTs = Math.max(...accountedList.map((s) => new Date(ciByStudent[s.id].timestamp).getTime()));
      allClearMs = lastTs - startMs;
    }

    // Per-class: lock-in = time from start until every student in the class was
    // accounted. Classes that never fully cleared are flagged.
    const groups = {};
    visible.forEach((s) => {
      const cid = classOf(s);
      (groups[cid] = groups[cid] || []).push(s);
    });
    const classes = Object.keys(groups).map((cid) => {
      const roster = groups[cid];
      const done = roster.filter((s) => ciByStudent[s.id]);
      const complete = done.length === roster.length;
      let lockInMs = null;
      if (complete && startMs != null) {
        const last = Math.max(...roster.map((s) => new Date(ciByStudent[s.id].timestamp).getTime()));
        lockInMs = last - startMs;
      }
      return {
        cid,
        className: classMap[cid]?.className || (cid === "null" ? "Unassigned" : "Class"),
        teacher: teacherMap[classMap[cid]?.teacherId] || "—",
        total: roster.length,
        done: done.length,
        complete,
        lockInMs,
      };
    });
    // Incomplete first (needed attention), then fastest lock-in first.
    classes.sort((a, b) => {
      if (a.complete !== b.complete) return a.complete ? 1 : -1;
      return (a.lockInMs ?? Infinity) - (b.lockInMs ?? Infinity);
    });

    return { total, accounted, missing, allClearMs, classes };
  }, [checkIns, students, classMap, teacherMap, activeClassIds, startMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDrill = emergency?.isDrill === true;
  const durationMs = startMs != null && endMs != null ? endMs - startMs : null;
  const pct = stats && stats.total ? Math.round((stats.accounted / stats.total) * 100) : 0;

  return (
    <div className="checklist-page">
      <div className="checklist-head">
        <div className="emg-mode-head">
          <div>
            <h2 style={{ margin: 0 }}>
              {isDrill ? "🟦 Drill Recap" : "🚨 Emergency Recap"}
            </h2>
            <div style={{ marginTop: 4 }}>
              <span className={`emg-kind-badge ${isDrill ? "is-drill" : "is-real"}`}>
                {isDrill ? "DRILL" : "EMERGENCY"}
              </span>
              <span style={{ fontSize: 13, color: "var(--color-secondary)", marginLeft: 10 }}>
                {fmtDateTime(emergency?.startedAt)}
                {emergency?.startedByName ? ` · started by ${emergency.startedByName}` : ""}
              </span>
            </div>
          </div>
          {onClose && (
            <button className="checklist-action" onClick={onClose}>Done</button>
          )}
        </div>
      </div>

      {stats == null ? (
        <p className="checklist-empty">Crunching the numbers…</p>
      ) : (
        <>
          {/* Headline stat cards */}
          <div className="emg-recap-cards">
            <div className="emg-recap-card">
              <div className="emg-recap-label">Duration</div>
              <div className="emg-recap-value">{fmtDuration(durationMs)}</div>
            </div>
            <div className="emg-recap-card">
              <div className="emg-recap-label">Accounted for</div>
              <div className="emg-recap-value">{stats.accounted} / {stats.total}</div>
              <div className="emg-recap-sub">{pct}%</div>
            </div>
            <div className="emg-recap-card">
              <div className="emg-recap-label">Time to all-clear</div>
              {stats.total > 0 && stats.missing === 0 ? (
                <div className="emg-recap-value">{fmtDuration(stats.allClearMs)}</div>
              ) : (
                <div className="emg-recap-value warn">
                  {stats.missing} never accounted
                </div>
              )}
            </div>
          </div>

          {/* Per-class lock-in */}
          <h3 style={{ margin: "8px 0 4px" }}>By class</h3>
          <div className="emg-recap-classes">
            {stats.classes.map((c) => (
              <div key={c.cid} className={`emg-recap-row ${c.complete ? "" : "incomplete"}`}>
                <div className="emg-recap-row-main">
                  <span className="emg-recap-row-name">{c.className}</span>
                  <span className="emg-recap-row-teacher">{c.teacher}</span>
                </div>
                <div className="emg-recap-row-right">
                  <span className="emg-recap-row-count">{c.done}/{c.total}</span>
                  {c.complete ? (
                    <span className="emg-recap-row-lock">cleared in {fmtDuration(c.lockInMs)}</span>
                  ) : (
                    <span className="emg-recap-row-lock warn">{c.total - c.done} missing</span>
                  )}
                </div>
              </div>
            ))}
            {stats.classes.length === 0 && <p className="checklist-empty">No classes to report.</p>}
          </div>
        </>
      )}
    </div>
  );
}
